import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUpIcon, TargetIcon, ZapIcon, Users2Icon, CheckIcon, ArrowRightIcon } from 'lucide-react';
import { Contact } from '../components/Contact';
import { Navigation } from '../components/Navigation';
import { NexyomLogo } from '../components/NexyomLogo';
const services = [{
  icon: TargetIcon,
  title: 'Meta Ads (Facebook & Instagram)',
  description: 'Campagnes publicitaires ciblées sur les réseaux sociaux les plus performants.',
  features: ['Ciblage précis par audience, comportement et intérêts', 'Création de visuels et copies optimisés pour la conversion', 'Tests A/B continus pour maximiser le ROI', 'Retargeting avancé pour récupérer les visiteurs perdus', 'Optimisation du coût par acquisition (CPA)'],
  results: "Jusqu'à 300% d'augmentation du ROAS"
}, {
  icon: ZapIcon,
  title: 'Google Ads (Search & Display)',
  description: 'Visibilité immédiate sur Google avec des campagnes Search et Display performantes.',
  features: ["Recherche de mots-clés à forte intention d'achat", 'Campagnes Search optimisées pour la conversion', 'Display et remarketing pour étendre la portée', 'Google Shopping pour e-commerce', 'Optimisation du Quality Score et réduction du CPC'],
  results: 'Taux de conversion moyen de 8-12%'
}, {
  icon: TrendingUpIcon,
  title: 'TikTok Ads',
  description: 'Exploitez le potentiel viral de TikTok pour toucher une audience jeune et engagée.',
  features: ['Création de contenu natif TikTok', 'Ciblage démographique et comportemental précis', 'Campagnes de notoriété et de conversion', 'Collaboration avec créateurs de contenu', "Optimisation pour l'algorithme TikTok"],
  results: 'CPM 40% inférieur à Facebook'
}, {
  icon: Users2Icon,
  title: 'LinkedIn Ads (B2B)',
  description: 'Ciblez les décideurs et professionnels avec des campagnes LinkedIn ultra-qualifiées.',
  features: ["Ciblage par poste, secteur, taille d'entreprise", 'Lead generation avec formulaires natifs', 'Sponsored Content et InMail', 'Account-Based Marketing (ABM)', 'Nurturing de leads B2B'],
  results: 'Leads B2B qualifiés à 85%+'
}];
const methodology = [{
  step: '1',
  title: 'Audit & Analyse',
  description: 'Analyse approfondie de votre marché, concurrence, et audiences cibles. Identification des opportunités de croissance.'
}, {
  step: '2',
  title: 'Stratégie & Planning',
  description: 'Définition des objectifs SMART, choix des plateformes, allocation budgétaire, et création du plan média.'
}, {
  step: '3',
  title: 'Création & Lancement',
  description: 'Production des créatifs (visuels, vidéos, copies), configuration des campagnes, et mise en ligne progressive.'
}, {
  step: '4',
  title: 'Optimisation Continue',
  description: 'Monitoring quotidien, tests A/B, ajustements des enchères, et optimisation du ciblage pour maximiser le ROI.'
}, {
  step: '5',
  title: 'Reporting & Scaling',
  description: 'Rapports détaillés avec KPIs clés, insights actionnables, et recommandations pour scaler les campagnes gagnantes.'
}];
const pricing = [{
  title: 'Pack Starter',
  price: '990€',
  suffix: 'HT / mois',
  details: 'Idéal pour démarrer',
  budget: 'Budget pub : 1 000€ - 3 000€/mois',
  features: ['1 plateforme (Meta ou Google)', 'Configuration complète des campagnes', 'Création de 3 créatifs/mois', 'Optimisation hebdomadaire', 'Reporting mensuel'],
  highlight: false
}, {
  title: 'Pack Growth',
  price: '1 990€',
  suffix: 'HT / mois',
  details: 'Pour scaler rapidement',
  budget: 'Budget pub : 3 000€ - 10 000€/mois',
  features: ['2 plateformes (Meta + Google)', 'Configuration avancée + retargeting', 'Création de 6 créatifs/mois', 'Optimisation bi-hebdomadaire', 'Reporting hebdomadaire + dashboard'],
  highlight: true
}, {
  title: 'Pack Enterprise',
  price: 'Sur mesure',
  suffix: '',
  details: 'Solution complète',
  budget: 'Budget pub : 10 000€+/mois',
  features: ['Multi-plateformes (Meta, Google, TikTok, LinkedIn)', 'Stratégie omnicanale complète', 'Créatifs illimités + production vidéo', 'Optimisation quotidienne', 'Account manager dédié + reporting temps réel'],
  highlight: false
}];
const kpis = [{
  metric: 'ROAS Moyen',
  value: '4.2x',
  description: 'Retour sur investissement publicitaire'
}, {
  metric: 'CPA Réduit',
  value: '-45%',
  description: 'Coût par acquisition optimisé'
}, {
  metric: 'Taux de Conversion',
  value: '8.5%',
  description: 'Conversion moyenne e-commerce'
}, {
  metric: 'Clients Actifs',
  value: '50+',
  description: 'Entreprises accompagnées'
}];
export function PerformanceMarketingPage() {
  return <div className="w-full bg-white" data-id="element-246">
      <Navigation data-id="element-247" />

      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center justify-center bg-black text-white overflow-hidden" data-id="element-248">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:80px_80px]" data-id="element-249" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/50" data-id="element-250" />

        {/* Accent geometric elements */}
        <div className="absolute top-32 right-20 w-32 h-32 border-2 border-blue-500/20" data-id="element-251" />
        <div className="absolute bottom-32 left-20 w-24 h-24 border-2 border-blue-500/10" data-id="element-252" />

        <div className="relative z-10 max-w-6xl mx-auto px-6 py-32 text-center" data-id="element-253">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          ease: [0.16, 1, 0.3, 1]
        }} data-id="element-254">
            <div className="inline-block px-4 py-2 bg-blue-500/10 border border-blue-500/20 text-blue-400 mb-8 font-medium tracking-widest uppercase text-sm" data-id="element-255">
              Marketing Digital
            </div>
            <h1 className="text-5xl md:text-7xl font-black tracking-tighter mb-8 leading-[1.1]" data-id="element-256">
              MARKETING DIGITAL
              <br data-id="element-257" />
              <span className="text-blue-500" data-id="element-258">DE PERFORMANCE</span>
            </h1>
          </motion.div>

          <motion.p initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.15,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-xl md:text-2xl text-gray-400 mb-14 max-w-3xl mx-auto font-light leading-relaxed" data-id="element-259">
            Générez des leads qualifiés et augmentez vos ventes avec des
            campagnes publicitaires data-driven sur Meta, Google, TikTok et
            LinkedIn.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.3,
          ease: [0.16, 1, 0.3, 1]
        }} data-id="element-260">
            <a href="#contact" className="group inline-flex items-center gap-3 bg-blue-500 text-white px-10 py-5 text-lg font-semibold transition-all duration-300 hover:bg-blue-600 relative overflow-hidden" data-id="element-261">
              <span className="relative z-10" data-id="element-262">Booster ma croissance</span>
              <ArrowRightIcon className="w-5 h-5 relative z-10 transition-transform duration-300 group-hover:translate-x-1" data-id="element-263" />
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-264" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* KPIs Section */}
      <section className="py-24 bg-white border-b border-gray-100" data-id="element-265">
        <div className="max-w-7xl mx-auto px-6" data-id="element-266">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-12" data-id="element-267">
            {kpis.map((kpi, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6,
            delay: index * 0.1,
            ease: [0.16, 1, 0.3, 1]
          }} className="text-center" data-id="element-268">
                <div className="text-5xl md:text-6xl font-black text-blue-500 mb-3 tracking-tight" data-id="element-269">
                  {kpi.value}
                </div>
                <div className="text-lg font-bold text-black mb-2 tracking-tight" data-id="element-270">
                  {kpi.metric}
                </div>
                <div className="text-sm text-gray-600 font-light" data-id="element-271">
                  {kpi.description}
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-40 bg-gray-50 relative" data-id="element-272">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" data-id="element-273" />

        <div className="max-w-7xl mx-auto px-6" data-id="element-274">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.8,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-center mb-24" data-id="element-275">
            <h2 className="text-5xl md:text-6xl font-black text-black mb-6 tracking-tight" data-id="element-276">
              Nos Plateformes
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto font-light" data-id="element-277">
              Expertise multi-plateformes pour maximiser votre portée et votre
              ROI
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-278">
            {services.map((service, index) => {
            const Icon = service.icon;
            return <motion.div key={index} initial={{
              opacity: 0,
              y: 40
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-100px'
            }} transition={{
              duration: 0.8,
              delay: index * 0.1,
              ease: [0.16, 1, 0.3, 1]
            }} className="bg-white p-10 border border-gray-200 transition-all duration-500 hover:shadow-2xl hover:border-blue-500/30" data-id="element-279">
                  <div className="w-16 h-16 bg-blue-500 flex items-center justify-center mb-6" data-id="element-280">
                    <Icon className="w-8 h-8 text-white" strokeWidth={1.5} data-id="element-281" />
                  </div>

                  <h3 className="text-2xl font-bold text-black mb-3 tracking-tight" data-id="element-282">
                    {service.title}
                  </h3>

                  <p className="text-gray-600 mb-6 font-light leading-relaxed" data-id="element-283">
                    {service.description}
                  </p>

                  <ul className="space-y-3 mb-6" data-id="element-284">
                    {service.features.map((feature, i) => <li key={i} className="flex items-start gap-3 text-gray-700" data-id="element-285">
                        <CheckIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" data-id="element-286" />
                        <span className="font-light text-sm leading-relaxed" data-id="element-287">
                          {feature}
                        </span>
                      </li>)}
                  </ul>

                  <div className="pt-6 border-t border-gray-100" data-id="element-288">
                    <p className="text-sm font-bold text-blue-600" data-id="element-289">
                      {service.results}
                    </p>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Methodology Section */}
      <section className="py-40 bg-white relative" data-id="element-290">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" data-id="element-291" />

        <div className="max-w-6xl mx-auto px-6" data-id="element-292">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.8,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-center mb-32" data-id="element-293">
            <h2 className="text-5xl md:text-6xl font-black text-black mb-6 tracking-tight" data-id="element-294">
              Notre Méthodologie
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto font-light" data-id="element-295">
              Un processus éprouvé pour des résultats mesurables
            </p>
          </motion.div>

          <div className="space-y-16" data-id="element-296">
            {methodology.map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.8,
            delay: index * 0.08,
            ease: [0.16, 1, 0.3, 1]
          }} className="flex gap-8 items-start" data-id="element-297">
                <div className="flex-shrink-0 w-20 h-20 bg-blue-500 flex items-center justify-center" data-id="element-298">
                  <span className="text-3xl font-black text-white" data-id="element-299">
                    {item.step}
                  </span>
                </div>
                <div className="flex-1 pt-2" data-id="element-300">
                  <h3 className="text-2xl font-bold text-black mb-3 tracking-tight" data-id="element-301">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 font-light leading-relaxed text-lg" data-id="element-302">
                    {item.description}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-40 bg-gray-50 relative" data-id="element-303">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" data-id="element-304" />

        <div className="max-w-7xl mx-auto px-6" data-id="element-305">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.8,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-center mb-24" data-id="element-306">
            <h2 className="text-5xl md:text-6xl font-black text-black mb-6 tracking-tight" data-id="element-307">
              Tarification Transparente
            </h2>
            <p className="text-xl text-gray-600 font-light" data-id="element-308">
              Des offres adaptées à votre budget publicitaire
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 items-start" data-id="element-309">
            {pricing.map((plan, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 40
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.8,
            delay: index * 0.1,
            ease: [0.16, 1, 0.3, 1]
          }} className={`relative p-10 bg-white border transition-all duration-500 hover:shadow-2xl ${plan.highlight ? 'border-blue-500 shadow-xl scale-105 z-10' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-310">
                {plan.highlight && <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-blue-500 text-white px-4 py-1 text-sm font-bold tracking-widest uppercase" data-id="element-311">
                    Recommandé
                  </div>}

                <h3 className="text-2xl font-bold text-black mb-4 tracking-tight" data-id="element-312">
                  {plan.title}
                </h3>
                <div className="flex items-baseline gap-1 mb-2" data-id="element-313">
                  <span className="text-4xl font-black text-black" data-id="element-314">
                    {plan.price}
                  </span>
                  {plan.suffix && <span className="text-gray-500 font-medium text-base" data-id="element-315">
                      {plan.suffix}
                    </span>}
                </div>
                <p className="text-sm text-gray-500 mb-3 font-medium uppercase tracking-wide" data-id="element-316">
                  {plan.details}
                </p>
                <p className="text-xs text-blue-600 font-bold mb-8" data-id="element-317">
                  {plan.budget}
                </p>

                <ul className="space-y-4 mb-10" data-id="element-318">
                  {plan.features.map((feature, i) => <li key={i} className="flex items-center gap-3 text-gray-700" data-id="element-319">
                      <CheckIcon className="w-5 h-5 text-blue-500 flex-shrink-0" data-id="element-320" />
                      <span className="font-light leading-relaxed" data-id="element-321">
                        {feature}
                      </span>
                    </li>)}
                </ul>
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="text-center mt-16" data-id="element-322">
            <p className="text-sm text-gray-500 font-light" data-id="element-323">
              * Hors budget publicitaire. Les frais de gestion sont facturés
              mensuellement.
            </p>
          </motion.div>
        </div>
      </section>

      <Contact data-id="element-324" />

      <footer className="bg-black text-white py-16 border-t border-white/10" data-id="element-325">
        <div className="max-w-6xl mx-auto px-6" data-id="element-326">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-327">
            <NexyomLogo variant="full" colorScheme="dark" size="sm" data-id="element-328" />
            <p className="text-gray-500 font-light tracking-wide" data-id="element-329">
              © 2025 Nexyom. Performance. Croissance. Ingénierie.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}